import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";

/**
 * OptimizedBgImage Component
 *
 * A reusable component for rendering background images with loading states
 *
 * @param {string} src - Image source URL
 * @param {string} alt - Alt text for accessibility
 * @param {string} height - Height of the container (default: '100%')
 * @param {string} borderRadius - Border radius (default: '0')
 * @param {ReactNode} children - Optional children to render inside
 * @param {object} ...props - Additional props passed to container
 *
 * @example
 * <OptimizedBgImage
 *   src={Images.AboutJustact}
 *   alt="About JustAct"
 *   height="100%"
 *   borderRadius="12px"
 * />
 */
const OptimizedBgImage = ({
  src,
  alt = "",
  height = "100%",
  borderRadius = "0",
  children,
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!src) {
      setHasError(true);
      return;
    }

    const img = new Image();

    img.onload = () => {
      setIsLoaded(true);
    };

    img.onerror = () => {
      setHasError(true);
      setIsLoaded(true);
    };

    img.src = src;

    // Cleanup
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src]);

  return (
    <ImageContainer height={height} borderRadius={borderRadius} {...props}>
      {!isLoaded && !hasError && <Skeleton />}
      <BackgroundImage
        bgImage={src}
        isLoaded={isLoaded}
        hasError={hasError}
        role="img"
        aria-label={alt}
      >
        {children}
      </BackgroundImage>
    </ImageContainer>
  );
};

// ==========================================
// Keyframe Animations
// ==========================================

const shimmer = keyframes`
  0% { 
    background-position: -1000px 0; 
  }
  100% { 
    background-position: 1000px 0; 
  }
`;

const fadeIn = keyframes`
  from { 
    opacity: 0; 
  }
  to { 
    opacity: 1; 
  }
`;

// ==========================================
// Styled Components
// ==========================================

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: ${(props) => props.height};
  border-radius: ${(props) => props.borderRadius};
  overflow: hidden;
  background: #f0f0f0;
`;

const Skeleton = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    #f0f0f0 0%,
    #e0e0e0 20%,
    #f0f0f0 40%,
    #f0f0f0 100%
  );
  background-size: 1000px 100%;
  animation: ${shimmer} 2s infinite linear;
  z-index: 1;
`;

const BackgroundImage = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background: ${(props) =>
    props.hasError
      ? "#e0e0e0"
      : `url(${props.bgImage}) center/cover no-repeat`};
  opacity: ${(props) => (props.isLoaded ? 1 : 0)};
  transition: opacity 0.4s ease-in-out;
  animation: ${(props) => (props.isLoaded ? fadeIn : "none")} 0.4s ease-in-out;
  z-index: 2;

  ${(props) =>
    props.hasError &&
    `
    display: flex;
    align-items: center;
    justify-content: center;
    
    &::after {
      content: '📷';
      font-size: 3rem;
      opacity: 0.3;
    }
  `}
`;

export default OptimizedBgImage;
